import type {
  IBulkRequest, IBulkResult, IRankRequest, IRecord, IRecordBatch, IRecordListParams, IRecordPage, IRecordStats, IRecordTypeLink, IRecordTypeLinkPage, IRecordViews,
  IRecordWrite, IStatBucket, ITransitionRequest,
} from '@platform/core/types';
import type { HttpClient } from './HttpClient';
import type { IDownload } from './IDownload';

/** Стеля зв'язків типу в одній відповіді: типова й максимальна для API — TypeLinksLimit.Max бекенду (Platform.Bl/Records/TypeLinksLimit.cs). */
const TYPE_LINKS_LIMIT = 5000;

/** Доступ до узагальненого API записів: один клас для всіх типів записів. */
export class RecordsDao {
  constructor(private readonly http: HttpClient) {}

  private base(ws: string, rt: string): string {
    return `/workspaces/${ws}/records/${encodeURIComponent(rt)}`;
  }

  list(ws: string, rt: string, params: IRecordListParams): Promise<IRecordPage> {
    return this.http.get<IRecordPage>(this.base(ws, rt), params as Record<string, unknown>);
  }

  /** Усі записи списку посторінково (сторінки по 100) до стелі `cap`; `total` — як повідомив API (для аналітичних подань). */
  async listAll(ws: string, rt: string, params: IRecordListParams = {}, cap = 1000): Promise<IRecordBatch> {
    const items: IRecord[] = [];
    let total = 0;
    for (let page = 1; items.length < cap; page += 1) {
      const chunk = await this.list(ws, rt, { ...params, page, pageSize: 100, facets: false });
      items.push(...chunk.items);
      total = chunk.total;
      if (chunk.items.length === 0 || items.length >= total) break;
    }
    return { items: items.slice(0, cap), total };
  }

  /**
   * Зв'язки всіх записів типу у воркспейсі (за бажанням — лише з іншим типом записів) до стелі 5000; `truncated` — рядків було більше.
   * Відповідь старого формату (масив) теж приймається: обрізаним вважається список довжиною у стелю.
   */
  async links(ws: string, rt: string, other?: string): Promise<IRecordTypeLinkPage> {
    const data = await this.http.get<IRecordTypeLinkPage | IRecordTypeLink[]>(`${this.base(ws, rt)}/links`, { other, limit: TYPE_LINKS_LIMIT });
    return Array.isArray(data) ? { items: data, truncated: data.length >= TYPE_LINKS_LIMIT } : data;
  }

  get(ws: string, rt: string, id: string): Promise<IRecord> {
    return this.http.get<IRecord>(`${this.base(ws, rt)}/${id}`);
  }

  create(ws: string, rt: string, body: IRecordWrite): Promise<IRecord> {
    return this.http.post<IRecord>(this.base(ws, rt), body);
  }

  patch(ws: string, rt: string, id: string, body: IRecordWrite, rowVersion?: number): Promise<IRecord> {
    return this.http.patch<IRecord>(`${this.base(ws, rt)}/${id}`, body, rowVersion);
  }

  transition(ws: string, rt: string, id: string, body: ITransitionRequest): Promise<IRecord> {
    return this.http.post<IRecord>(`${this.base(ws, rt)}/${id}/transition`, body);
  }

  rank(ws: string, rt: string, id: string, body: IRankRequest): Promise<IRecord> {
    return this.http.post<IRecord>(`${this.base(ws, rt)}/${id}/rank`, body);
  }

  remove(ws: string, rt: string, id: string): Promise<void> {
    return this.http.delete(`${this.base(ws, rt)}/${id}`);
  }

  stats(ws: string, rt: string, by: string, params: IRecordListParams): Promise<IRecordStats> {
    return this.http.get<IRecordStats>(`${this.base(ws, rt)}/stats`, { by, ...params } as Record<string, unknown>);
  }

  /**
   * Фасети за кількома полями (лічильники груп меню «за значеннями поля»): запит статистики на кожне поле, паралельно. Поле, статистика
   * якого не вдалася, у результат не потрапляє — меню тоді показує варіанти поля без лічильників.
   */
  async facets(ws: string, rt: string, fields: readonly string[]): Promise<Record<string, IStatBucket[]>> {
    const settled = await Promise.allSettled(fields.map((by) => this.stats(ws, rt, by, {})));
    const result: Record<string, IStatBucket[]> = {};
    settled.forEach((r, i) => {
      const field = fields[i];
      if (field !== undefined && r.status === 'fulfilled') result[field] = r.value.buckets;
    });
    return result;
  }

  bulk(ws: string, rt: string, body: IBulkRequest): Promise<IBulkResult> {
    return this.http.post<IBulkResult>(`${this.base(ws, rt)}/bulk`, body);
  }

  /** CSV-експорт списку з параметрами списку (фільтр, пресет, пошук, сортування, вид) і заголовками авторизації; ім'я — з відповіді або `<тип>.csv`. */
  exportCsv(ws: string, rt: string, params: IRecordListParams): Promise<IDownload> {
    return this.http.download(`${this.base(ws, rt)}/export`, params as Record<string, unknown>, `${rt}.csv`);
  }

  /** Лічильники переглядів записів типу (V010; база знань: «N переглядів», «Найпопулярніші»): записи з переглядами, найпопулярніші першими. */
  views(ws: string, rt: string): Promise<IRecordViews[]> {
    return this.http.get<IRecordViews[]>(`${this.base(ws, rt)}/views`);
  }

  /** Відкриття запису (стаття з бібліотеки): +1 перегляд → новий лічильник. */
  addView(id: string): Promise<IRecordViews> {
    return this.http.post<IRecordViews>(`/records/${id}/views`, {});
  }
}
